/**
 * P-6 日報設定 — ノード方針 (`/settings/daily-report/node-policies`、§14-2)。
 * 項目 × ノードの表示・必須制御 (継承) を設定する。
 * バックエンド: apps/api/src/routes/daily-reports.ts GET/PUT /daily-reports/config/node-policies。
 * 設計: docs/design/v05-daily-report-detail.md §3-4 / §14-2
 *
 * ⚠️ condition_set_id (visibility='conditional') の選択候補一覧APIは、日報向けの usage_point_id
 * ('daily_report_visibility') が condition_usage_points に未登録のため実質空になる (§10 #9-b、
 * config-validation.ts②)。P-5 の visibility_class_id と同様、既知のIDを直接入力する形にとどめる。
 * ⚠️ Lite は condition_set_id を常に禁止 (D-482、node-policies PUT が400で拒否)。
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/Button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { InheritanceSourceBadge, type InheritanceSourceKind } from '@/components/InheritanceSourceBadge';
import { useScopeStore, type NodesResponse } from '@/stores/scope';
import { flattenNodes } from '@/lib/node-tree';
import {
  fetchDailyReportConfigFields, fetchDailyReportNodePolicies, saveDailyReportNodePolicy,
  type DailyReportConfigField, type DailyReportNodePolicy,
} from '@/lib/daily-report-api';

type ResolvedWorkSectionVisible = {
  value: boolean | null;
  source_kind: InheritanceSourceKind;
  source_node_label: string | null;
};

/** I-7 (§10、D-1029): 「勤務実績の欄」の表示ON/OFF。Full限定 (Liteには欄という概念自体が無い)。
 *  PreferenceInputModePanel (ShiftPatternsSettingsPage.tsx) と同型: 値の解決は汎用の
 *  GET /inheritable-settings/:key/resolve、書込みは専用 PUT。 */
function WorkSectionVisibilityPanel({ nodeId }: { nodeId: string }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<ResolvedWorkSectionVisible>({
    queryKey: ['daily-report-work-section-visible', nodeId],
    queryFn: () => apiFetch(`/inheritable-settings/daily_report_work_section_visible/resolve?node_id=${encodeURIComponent(nodeId)}`),
    enabled: !!nodeId,
  });
  const initial = useRef<boolean | null>(null);
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    if (!data) return;
    const seeded = data.value !== false;
    initial.current = seeded;
    setVisible(seeded);
  }, [data]);

  const saveMut = useMutation({
    mutationFn: () => apiFetch<{ warnings: { code: string }[] }>('/daily-reports/config/work-section-visibility', {
      method: 'PUT',
      body: JSON.stringify({ node_id: nodeId, visible }),
    }),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['daily-report-work-section-visible'] });
      if (result.warnings.some((w) => w.code === 'daily_report_locked_conflict')) {
        toast.warning('この店舗は月次入力側で「日報優先 (locked)」の設定のままです。日報からもこの欄が非表示になるため、どちらからも入力できなくなります。月次入力側の設定もあわせて見直してください。');
      } else {
        toast.success('保存しました');
      }
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  const dirty = !!data && initial.current !== null && visible !== initial.current;

  return (
    <div className="border border-surface-border rounded-lg bg-white p-3 space-y-2">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-semibold text-text-body">勤務実績の欄</h3>
        {data && <InheritanceSourceBadge sourceKind={data.source_kind} sourceNodeLabel={data.source_node_label} />}
      </div>
      {isLoading || !data ? (
        <p className="text-sm text-text-sub">読み込み中…</p>
      ) : (
        <>
          <label className="flex items-center gap-2 text-sm text-text-body min-h-[44px]">
            <input
              type="checkbox"
              className="min-h-[20px] min-w-[20px]"
              checked={visible}
              onChange={(e) => setVisible(e.target.checked)}
            />
            日報にこの欄を表示する
          </label>
          {!visible && (
            <p className="text-xs text-text-sub">
              勤務実績は<Link to="/shift/monthly-entry" className="text-text-body underline">月次入力</Link>から入力できます。
            </p>
          )}
          <div className="flex justify-end">
            <Button variant="primary" size="sm" disabled={!dirty || saveMut.isPending} loading={saveMut.isPending} onClick={() => saveMut.mutate()}>
              保存
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

type Visibility = 'hidden' | 'optional' | 'required' | 'conditional' | 'readonly';
const VISIBILITY_LABELS: Record<Visibility, string> = {
  hidden: '非表示', optional: '任意入力', required: '必須', conditional: '条件付き必須', readonly: '読み取り専用',
};
const VISIBILITIES: Visibility[] = ['hidden', 'optional', 'required', 'conditional', 'readonly'];

const inputCls = 'w-full min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring';

type RowState = { visibility: Visibility | ''; conditionSetId: string; dirty: boolean };

export function DailyReportNodePoliciesSettingsPage() {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const isLite = user?.edition === 'lite';
  const scopeRoots = useScopeStore((s) => s.roots);
  const globalSelectedNodeId = useScopeStore((s) => s.selectedNodeId);
  const { data: nodesData } = useQuery<NodesResponse>({ queryKey: ['nodes'], queryFn: () => apiFetch<NodesResponse>('/nodes') });
  const flatNodes = useMemo(() => flattenNodes(nodesData?.roots ?? scopeRoots), [nodesData, scopeRoots]);

  const [nodeId, setNodeId] = useState('');
  const effectiveNodeId = nodeId || globalSelectedNodeId || flatNodes[0]?.node.node_id || '';

  const { data: fields = [] } = useQuery<DailyReportConfigField[]>({
    queryKey: ['daily-report-config-fields'], queryFn: fetchDailyReportConfigFields,
  });
  const activeFields = useMemo(
    () => [...fields].filter((f) => f.status === 'active' && f.value_kind !== 'computed')
      .sort((a, b) => a.section_order - b.section_order || a.sort_order - b.sort_order),
    [fields],
  );

  const { data: policies = [], isLoading } = useQuery<DailyReportNodePolicy[]>({
    queryKey: ['daily-report-node-policies', effectiveNodeId],
    queryFn: () => fetchDailyReportNodePolicies(effectiveNodeId),
    enabled: !!effectiveNodeId,
  });
  const policyByField = useMemo(() => new Map(policies.map((p) => [p.field_definition_id, p])), [policies]);

  const [rows, setRows] = useState<Map<string, RowState>>(new Map());
  // ⚠️ 店舗切替後、初回のデータ取得が完了した瞬間だけローカル編集状態を初期化する
  // (test-runnerレビューHIGH是正)。以前は `${effectiveNodeId}:${policies.length}` を再初期化の
  // 条件にしており、①1行だけ保存してpoliciesの件数が変わる(未設定→設定で+1)たびにも発火し、
  // 他の未保存(dirty)な行の入力内容が警告なくサーバー値で上書きされる、②逆に店舗切替直後
  // (isLoading中、policies=[]でeffectiveNodeIdは既に新店舗)にeffectiveNodeIdだけをキーにすると
  // 空データで即座に確定してしまい、実データ到着後に再初期化されない、の両方の問題があった。
  // ⇒ 「この店舗のデータを isLoading===false で見るのは初めてか」をrefで管理する。
  const seededNodeRef = useRef<string | null>(null);
  useEffect(() => {
    if (!effectiveNodeId || isLoading || seededNodeRef.current === effectiveNodeId) return;
    seededNodeRef.current = effectiveNodeId;
    setRows(new Map(activeFields.map((f) => {
      const p = policyByField.get(f.field_definition_id);
      return [f.field_definition_id, { visibility: (p?.visibility as Visibility) ?? '', conditionSetId: p?.condition_set_id ?? '', dirty: false }];
    })));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 初回ハイドレートのみ (seededNodeRef ガードで1回化)
  }, [effectiveNodeId, isLoading]);

  const saveMut = useMutation({
    mutationFn: ({ fieldId, row }: { fieldId: string; row: RowState }) => saveDailyReportNodePolicy({
      field_definition_id: fieldId, node_id: effectiveNodeId,
      visibility: row.visibility as Exclude<Visibility, ''>,
      condition_set_id: row.visibility === 'conditional' ? (row.conditionSetId.trim() || null) : null,
    }),
    onSuccess: (_data, { fieldId }) => {
      qc.invalidateQueries({ queryKey: ['daily-report-node-policies', effectiveNodeId] });
      setRows((prev) => { const next = new Map(prev); const r = next.get(fieldId); if (r) next.set(fieldId, { ...r, dirty: false }); return next; });
      toast.success('保存しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  const updateRow = (fieldId: string, patch: Partial<RowState>) => {
    setRows((prev) => { const next = new Map(prev); const cur = next.get(fieldId) ?? { visibility: '', conditionSetId: '', dirty: false }; next.set(fieldId, { ...cur, ...patch, dirty: true }); return next; });
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-h2">ノード方針</h2>
        <p className="text-sm text-text-sub mt-1">店舗ごとに項目の表示・必須を設定します。未設定の項目は既定の挙動になります。</p>
      </div>

      <div className="w-full sm:w-72">
        <label className="block text-sm text-text-sub mb-1">対象店舗</label>
        <Select value={effectiveNodeId} onValueChange={setNodeId}>
          <SelectTrigger aria-label="対象店舗"><SelectValue placeholder="店舗を選択してください" /></SelectTrigger>
          <SelectContent>
            {flatNodes.map(({ node, depth }) => (
              <SelectItem key={node.node_id} value={node.node_id}>{'　'.repeat(depth)}{node.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* I-7 (§10、D-1029): Full限定 (Liteには「勤務実績の欄」という概念自体が無い)。 */}
      {effectiveNodeId && !isLite && <WorkSectionVisibilityPanel nodeId={effectiveNodeId} />}

      {!effectiveNodeId ? (
        <p className="text-sm text-text-sub">店舗を選択してください。</p>
      ) : isLoading ? (
        <p className="text-sm text-text-sub">読み込み中…</p>
      ) : activeFields.length === 0 ? (
        <p className="text-sm text-text-sub">有効な項目がありません。先に「項目定義」で項目を登録してください。</p>
      ) : (
        <ul className="space-y-2">
          {activeFields.map((f) => {
            const row = rows.get(f.field_definition_id) ?? { visibility: '', conditionSetId: '', dirty: false };
            const canSaveRow = row.visibility !== '' && row.dirty
              && !(row.visibility === 'conditional' && (isLite || !row.conditionSetId.trim()));
            return (
              <li key={f.field_definition_id} className="border border-surface-border rounded-lg bg-white p-3 space-y-2">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <p className="text-sm font-medium text-text-body break-words">{f.label ?? f.field_code}</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Select value={row.visibility || '__unset__'} onValueChange={(v) => updateRow(f.field_definition_id, { visibility: v === '__unset__' ? '' : (v as Visibility) })}>
                      <SelectTrigger className="w-40" aria-label={`${f.label ?? f.field_code}: 表示設定`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__unset__">未設定</SelectItem>
                        {VISIBILITIES.map((v) => <SelectItem key={v} value={v}>{VISIBILITY_LABELS[v]}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button variant="primary" size="sm" disabled={!canSaveRow || saveMut.isPending}
                      loading={saveMut.isPending} onClick={() => saveMut.mutate({ fieldId: f.field_definition_id, row })}>保存</Button>
                  </div>
                </div>
                {row.visibility === 'conditional' && !isLite && (
                  <div>
                    <label className="block text-xs text-text-sub mb-1">条件セットID</label>
                    <input value={row.conditionSetId} onChange={(e) => updateRow(f.field_definition_id, { conditionSetId: e.target.value })}
                      className={inputCls} placeholder="既知の条件セットIDを入力してください" />
                  </div>
                )}
                {row.visibility === 'conditional' && isLite && (
                  <p className="text-xs text-alert-text">Lite プランでは条件付き表示は使用できません。別の設定を選択してください。</p>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
